import { FC, useEffect } from 'react';
import {
	Box,
	Button,
	Checkbox,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControlLabel,
	TextField,
} from '@mui/material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useDispatch, useSelector } from 'react-redux';
import { resetSaveCountrySuccess, updateCountryRequest } from 'redux/reducers/country/reducer';
import { getCountrySaveLoading, getCountrySaveSuccess } from 'redux/reducers/country/selectors';
import { TCountryDialogProps } from './types';

/** Formik keeps optional numbers as strings, so empty input has to become `null` for the API. */
const toNullableNumber = (value: string | number): number | null =>
	value === '' || value === null ? null : Number(value);

const nullableNumber = () =>
	Yup.number()
		.transform((value, original) => (original === '' ? null : value))
		.nullable();

const validationSchema = Yup.object({
	name: Yup.string().required('Name is required').max(255, 'Max 255 characters'),
	isoAlpha2: Yup.string()
		.required('ISO Alpha-2 is required')
		.length(2, 'Must be exactly 2 characters'),
	isoAlpha3: Yup.string()
		.required('ISO Alpha-3 is required')
		.length(3, 'Must be exactly 3 characters'),
	isoNumeric: Yup.number()
		.required('ISO Numeric is required')
		.integer('Must be an integer')
		.min(0, 'Must be >= 0'),
	currency: Yup.string().max(3, 'Max 3 characters'),
	thresholdCurrency: Yup.string().max(3, 'Max 3 characters'),
	customsThreshold: nullableNumber().min(0, 'Must be >= 0'),
	easFeeMultiplierPerc: nullableNumber().min(0, 'Must be >= 0'),
	additionalCustomsProcedure: Yup.string().max(255, 'Max 255 characters'),
	codeForFiscalReports: Yup.string().max(255, 'Max 255 characters'),
});

export const CountryDialog: FC<TCountryDialogProps> = ({ open, onClose, countryToEdit }) => {
	const dispatch = useDispatch();

	const loading = useSelector(getCountrySaveLoading);
	const saveSuccess = useSelector(getCountrySaveSuccess);

	const formik = useFormik({
		initialValues: {
			name: countryToEdit?.name ?? '',
			isoAlpha2: countryToEdit?.isoAlpha2 ?? '',
			isoAlpha3: countryToEdit?.isoAlpha3 ?? '',
			isoNumeric: countryToEdit?.isoNumeric ?? ('' as unknown as number),
			currency: countryToEdit?.currency ?? '',
			customsThreshold: countryToEdit?.customsThreshold ?? ('' as unknown as number),
			thresholdCurrency: countryToEdit?.thresholdCurrency ?? '',
			easFeeMultiplierPerc: countryToEdit?.easFeeMultiplierPerc ?? ('' as unknown as number),
			additionalCustomsProcedure: countryToEdit?.additionalCustomsProcedure ?? '',
			codeForFiscalReports: countryToEdit?.codeForFiscalReports ?? '',
			isEu: countryToEdit?.isEu ?? false,
			isSupported: countryToEdit?.isSupported ?? false,
			isCustomDutySupported: countryToEdit?.isCustomDutySupported ?? false,
			isArticle194Applied: countryToEdit?.isArticle194Applied ?? false,
			isSpecialDesignatedRateApplied: countryToEdit?.isSpecialDesignatedRateApplied ?? false,
		},
		enableReinitialize: true,
		validationSchema,
		onSubmit: (values) => {
			if (!countryToEdit) return;
			const body = {
				name: values.name,
				isEu: values.isEu,
				isoAlpha2: values.isoAlpha2,
				isoAlpha3: values.isoAlpha3,
				isoNumeric: Number(values.isoNumeric),
				currency: values.currency,
				isSupported: values.isSupported,
				isArticle194Applied: values.isArticle194Applied,
				isSpecialDesignatedRateApplied: values.isSpecialDesignatedRateApplied,
				additionalCustomsProcedure: values.additionalCustomsProcedure,
				customsThreshold: toNullableNumber(values.customsThreshold),
				thresholdCurrency: values.thresholdCurrency,
				easFeeMultiplierPerc: toNullableNumber(values.easFeeMultiplierPerc),
				codeForFiscalReports: values.codeForFiscalReports,
				isCustomDutySupported: values.isCustomDutySupported,
			};
			dispatch(updateCountryRequest({ id: countryToEdit.id, body }));
		},
	});

	useEffect(() => {
		if (saveSuccess) {
			dispatch(resetSaveCountrySuccess());
			formik.resetForm();
			onClose();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [saveSuccess]);

	const handleClose = () => {
		formik.resetForm();
		onClose();
	};

	return (
		<Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
			<DialogTitle>Edit Country</DialogTitle>
			<DialogContent>
				<Box
					component="form"
					onSubmit={formik.handleSubmit}
					sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}
				>
					<TextField
						label="Name"
						name="name"
						size="small"
						value={formik.values.name}
						onChange={formik.handleChange}
						onBlur={formik.handleBlur}
						error={formik.touched.name && !!formik.errors.name}
						helperText={formik.touched.name && formik.errors.name}
					/>
					<Box sx={{ display: 'flex', gap: 2 }}>
						<TextField
							label="ISO Alpha-2"
							name="isoAlpha2"
							size="small"
							fullWidth
							value={formik.values.isoAlpha2}
							onChange={formik.handleChange}
							onBlur={formik.handleBlur}
							error={formik.touched.isoAlpha2 && !!formik.errors.isoAlpha2}
							helperText={formik.touched.isoAlpha2 && formik.errors.isoAlpha2}
						/>
						<TextField
							label="ISO Alpha-3"
							name="isoAlpha3"
							size="small"
							fullWidth
							value={formik.values.isoAlpha3}
							onChange={formik.handleChange}
							onBlur={formik.handleBlur}
							error={formik.touched.isoAlpha3 && !!formik.errors.isoAlpha3}
							helperText={formik.touched.isoAlpha3 && formik.errors.isoAlpha3}
						/>
						<TextField
							label="ISO Numeric"
							name="isoNumeric"
							type="number"
							size="small"
							fullWidth
							value={formik.values.isoNumeric}
							onChange={formik.handleChange}
							onBlur={formik.handleBlur}
							error={formik.touched.isoNumeric && !!formik.errors.isoNumeric}
							helperText={formik.touched.isoNumeric && formik.errors.isoNumeric}
						/>
					</Box>
					<TextField
						label="Currency"
						name="currency"
						size="small"
						value={formik.values.currency}
						onChange={formik.handleChange}
						onBlur={formik.handleBlur}
						error={formik.touched.currency && !!formik.errors.currency}
						helperText={formik.touched.currency && formik.errors.currency}
					/>
					<Box sx={{ display: 'flex', gap: 2 }}>
						<TextField
							label="Customs Threshold"
							name="customsThreshold"
							type="number"
							size="small"
							fullWidth
							inputProps={{ step: '0.01' }}
							value={formik.values.customsThreshold}
							onChange={formik.handleChange}
							onBlur={formik.handleBlur}
							error={formik.touched.customsThreshold && !!formik.errors.customsThreshold}
							helperText={formik.touched.customsThreshold && formik.errors.customsThreshold}
						/>
						<TextField
							label="Threshold Currency"
							name="thresholdCurrency"
							size="small"
							fullWidth
							value={formik.values.thresholdCurrency}
							onChange={formik.handleChange}
							onBlur={formik.handleBlur}
							error={formik.touched.thresholdCurrency && !!formik.errors.thresholdCurrency}
							helperText={formik.touched.thresholdCurrency && formik.errors.thresholdCurrency}
						/>
					</Box>
					<TextField
						label="EAS Fee Multiplier (%)"
						name="easFeeMultiplierPerc"
						type="number"
						size="small"
						inputProps={{ step: '0.01' }}
						value={formik.values.easFeeMultiplierPerc}
						onChange={formik.handleChange}
						onBlur={formik.handleBlur}
						error={formik.touched.easFeeMultiplierPerc && !!formik.errors.easFeeMultiplierPerc}
						helperText={formik.touched.easFeeMultiplierPerc && formik.errors.easFeeMultiplierPerc}
					/>
					<TextField
						label="Additional Customs Procedure"
						name="additionalCustomsProcedure"
						size="small"
						value={formik.values.additionalCustomsProcedure}
						onChange={formik.handleChange}
						onBlur={formik.handleBlur}
						error={
							formik.touched.additionalCustomsProcedure &&
							!!formik.errors.additionalCustomsProcedure
						}
						helperText={
							formik.touched.additionalCustomsProcedure && formik.errors.additionalCustomsProcedure
						}
					/>
					<TextField
						label="Code For Fiscal Reports"
						name="codeForFiscalReports"
						size="small"
						value={formik.values.codeForFiscalReports}
						onChange={formik.handleChange}
						onBlur={formik.handleBlur}
						error={formik.touched.codeForFiscalReports && !!formik.errors.codeForFiscalReports}
						helperText={formik.touched.codeForFiscalReports && formik.errors.codeForFiscalReports}
					/>
					<FormControlLabel
						control={
							<Checkbox name="isEu" checked={formik.values.isEu} onChange={formik.handleChange} />
						}
						label="EU"
					/>
					<FormControlLabel
						control={
							<Checkbox
								name="isSupported"
								checked={formik.values.isSupported}
								onChange={formik.handleChange}
							/>
						}
						label="Supported"
					/>
					<FormControlLabel
						control={
							<Checkbox
								name="isCustomDutySupported"
								checked={formik.values.isCustomDutySupported}
								onChange={formik.handleChange}
							/>
						}
						label="Custom Duty Supported"
					/>
					<FormControlLabel
						control={
							<Checkbox
								name="isArticle194Applied"
								checked={formik.values.isArticle194Applied}
								onChange={formik.handleChange}
							/>
						}
						label="Article 194 Applied"
					/>
					<FormControlLabel
						control={
							<Checkbox
								name="isSpecialDesignatedRateApplied"
								checked={formik.values.isSpecialDesignatedRateApplied}
								onChange={formik.handleChange}
							/>
						}
						label="Special Designated Rate Applied"
					/>
				</Box>
			</DialogContent>
			<DialogActions>
				<Button onClick={handleClose} disabled={loading}>
					Cancel
				</Button>
				<Button
					onClick={() => formik.handleSubmit()}
					variant="contained"
					color="primary"
					disabled={loading}
					startIcon={loading ? <CircularProgress size={16} /> : undefined}
				>
					Save
				</Button>
			</DialogActions>
		</Dialog>
	);
};
